import { io } from "socket.io-client";
import { getCookie } from "./utils/cookieUtils";
import {
  ACCESS_TOKEN_COOKIE_KEY,
  ADMIN_ACCESS_TOKEN_COOKIE_KEY,
  ADMINS_COACH_ID_COOKIE_KEY,
} from "./data/enums/TokenEnums";

const URL = process.env.REACT_APP_NEST_BASE_URL;
export const socket = io(URL, {
  withCredentials: true,
  extraHeaders: {
    // authorization: getCookie(ACCESS_TOKEN_COOKIE_KEY)
  },
  autoConnect: false,
});

// ─── Handshake auth ──────────────────────────────────────────────────────────
// One resolver for every namespace, read at CONNECT time rather than at module
// load (a value captured at import time would predate login on a cold start);
// `connect()`/`updateAuth()` set it on every handshake.
//
// Two things about the value, both deliberate and both contradicting a doc:
//
// 1. It is the BARE token, with no `Bearer ` prefix — unlike every axios call
//    in this repo, which sends `Bearer ${token}` via `_resolveAuthHeader`.
//    `12-live-review-fe-handoff.md` §5 and `08-fe-handoff.md`:219 both specify
//    `Bearer` on the socket handshake. Bare is used anyway because it is what
//    every WORKING socket in both apps actually sends: the old coach Project
//    namespaces (removed, CD30) shipped like this, and the CEO app's `/new-chat` namespace is bare
//    too. Same story as the REST auth, where live testing beat the handoff and
//    the comment in `executionBlueprintStore` now warns against "fixing" it
//    back. If `/live-review` disconnects immediately after connecting, that is
//    the auth failure §5 describes and THIS is the line to flip — the socket
//    store logs a message naming this exact ambiguity when it sees that.
//
// 2. It carries the admin-impersonating-a-coach fallback, which the previous
//    single-cookie read did not. That path is a real coach workflow (see
//    CLAUDE.md) and would otherwise send `authorization: undefined`. This is
//    strictly additive: when `coachAccessToken` exists the resolved value is
//    identical to before.
//    `adminsCoachId` is a guard only — it is never sent, matching the REST
//    resolver.
const resolveSocketAuthToken = () => {
  const accessToken = getCookie(ACCESS_TOKEN_COOKIE_KEY);
  if (accessToken) return accessToken;
  const adminAccessToken = getCookie(ADMIN_ACCESS_TOKEN_COOKIE_KEY);
  const adminsCoachId = getCookie(ADMINS_COACH_ID_COOKIE_KEY);
  if (adminAccessToken && adminsCoachId) return adminAccessToken;
  return null;
};

// Execution Blueprint Live Review — the `/live-review` namespace
// (`12-live-review-fe-handoff.md` §5). Deliberately its own connection and NOT
// the Interview chat socket: it is a different namespace with a different event
// vocabulary, opened fresh when a coach lands on the Live Review screen.
//
// `URL` already ends in a slash (every REACT_APP_*_URL in .env does), so the
// namespace is appended bare. Adding a leading slash here yields a double slash
// and a handshake that fails with no useful error.
//
// This is the first namespace in this repo shared with the CEO app — the same
// backend namespace serves both, and it is the one place where two different
// logged-in people are in the same room. `authorization` is set here only as a
// placeholder; `connect()` resolves the real value per handshake.
export const liveReviewSocket = io(`${URL}live-review`, {
  transports: ["websocket"],
  autoConnect: false,
  withCredentials: true,
  extraHeaders: {
    authorization: resolveSocketAuthToken(),
  },
});

// Annual OKR Live Review — the `/annual-okr-live-review` namespace (Annual OKR
// handoff §7). Its OWN namespace and its own connection, deliberately not
// reusing `/live-review` above: the two share a shape but not a vocabulary —
// `join_annual_okr_live_review` vs `join_live_review`, Objectives vs blueprint
// items — and one socket answering to both would make a stray event from the
// wrong feature look like a protocol error rather than a routing mistake.
//
// This is the second namespace shared with the CEO app. Same `URL`-ends-in-a-
// slash rule as above: a leading slash here yields a double slash and a
// handshake that fails with no useful error.
export const annualOkrLiveReviewSocket = io(`${URL}annual-okr-live-review`, {
  transports: ["websocket"],
  autoConnect: false,
  withCredentials: true,
  extraHeaders: {
    authorization: resolveSocketAuthToken(),
  },
});

// Quarterly OKR Live Review — the `/quarterly-okr-live-review` namespace
// (Quarterly OKR handoff §10). Its own connection for the same reason as the
// Annual one above; same no-leading-slash rule.
export const quarterlyOkrLiveReviewSocket = io(
  `${URL}quarterly-okr-live-review`,
  {
    transports: ["websocket"],
    autoConnect: false,
    withCredentials: true,
    extraHeaders: {
      authorization: resolveSocketAuthToken(),
    },
  },
);

// OKR Plans coach chats — the shared `/new-chat` namespace in COACH MODE
// (OKR Plans BE_HANDOFF.md §2.8, FE58): the coach's token and NO `sme_id`,
// so only coach chats (`okr_plan_coach`, `okr_plan_coach_final`) work on it.
// Same bare-token handshake as every other namespace here; resolved per
// connect in `connect()` below. No leading slash (see above).
export const coachChatSocket = io(`${URL}new-chat`, {
  transports: ["websocket"],
  autoConnect: false,
  withCredentials: true,
  extraHeaders: {
    authorization: resolveSocketAuthToken(),
  },
});

// Unified Project Socket Manager
// This ensures all three sockets stay connected and only connects them once
class UnifiedProjectSocketManager {
  constructor() {
    this.connectionCount = 0;
    this.connectedNamespaces = new Set();
    this.instrumentedNamespaces = new Set();
  }

  attachLifecycleLogs(socket, namespace) {
    const prefix = `[UnifiedSocket:${namespace}]`;
    const ts = () => new Date().toISOString();
    const log = (...args) => {
      try {
        console.log(prefix, ts(), ...args);
      } catch (e) {}
    };
    // if (!socket) return;
    // try {
    //   socket.on("connect", () => log("connect", socket.id));
    //   socket.on("disconnect", (reason) => log("disconnect", reason));
    //   socket.on("connect_error", (err) =>
    //     log("connect_error", err?.message || err),
    //   );
    //   socket.on("error", (err) => log("error", err?.message || err));
    //   socket.onAny((event) => {
    //     if (typeof event === "string") {
    //       log("event", event);
    //     }
    //   });
    // } catch (e) {}
    // try {
    //   socket.io.on("reconnect_attempt", (attempt) =>
    //     log("reconnect_attempt", attempt),
    //   );
    //   socket.io.on("reconnect", (attempt) => log("reconnect", attempt));
    //   socket.io.on("reconnect_error", (err) =>
    //     log("reconnect_error", err?.message || err),
    //   );
    //   socket.io.on("reconnect_failed", () => log("reconnect_failed"));
    //   socket.io.on("error", (err) => log("manager_error", err?.message || err));
    // } catch (e) {}
    try {
      const engine = socket.io.engine;
      if (engine && engine.on) {
        engine.on("open", () => log("engine_open"));
        engine.on("close", (reason) => log("engine_close", reason));
        engine.on("error", (err) => log("engine_error", err?.message || err));
        engine.on("upgrade", (transport) =>
          log("engine_upgrade", transport?.name || transport),
        );
      }
    } catch (e) {}
  }

  // Connect a namespace (only if not already connected)
  connect(namespace) {
    let socket;
    switch (namespace) {
      case "live-review":
        socket = liveReviewSocket;
        break;
      case "annual-okr-live-review":
        socket = annualOkrLiveReviewSocket;
        break;
      case "quarterly-okr-live-review":
        socket = quarterlyOkrLiveReviewSocket;
        break;
      case "new-chat":
        socket = coachChatSocket;
        break;
      default:
        return null;
    }

    // Update auth token. Resolved fresh on every connect so a token refresh
    // (or an admin starting to impersonate a coach) can't leave a stale copy on
    // the handshake.
    socket.io.opts.extraHeaders = {
      authorization: resolveSocketAuthToken(),
    };

    // Only connect if not already connected
    if (!socket.connected) {
      socket.connect();
      this.connectedNamespaces.add(namespace);
    }
    if (!this.instrumentedNamespaces.has(namespace)) {
      this.attachLifecycleLogs(socket, namespace);
      this.instrumentedNamespaces.add(namespace);
    }

    this.connectionCount++;
    return socket;
  }

  // "Disconnect" - but actually don't disconnect, just decrement counter
  // Only actually disconnect when leaving Projects section entirely
  disconnect(namespace) {
    this.connectionCount = Math.max(0, this.connectionCount - 1);
    // Don't actually disconnect - keep connections alive
    return this.getSocket(namespace);
  }

  // Actually close one namespace's transport, which `disconnect()` above does
  // not do — it only decrements a counter, so a "disconnected" Project socket
  // stays open and keeps receiving.
  //
  // That is right for the Project feature (the user tabs between screens that
  // all share one live stream) and wrong for Live Review, where leaving the
  // screen has to end the session: the backend derives PRESENCE from the
  // connection, so a socket left open reports the coach as still in the room
  // after they have navigated away, and the CEO sees "Coach online" for a coach
  // who closed the tab. Presence is the one piece of state a lingering socket
  // actively falsifies, which is why this exists.
  //
  // Also drops the namespace from `connectedNamespaces` so a later `connect()`
  // is treated as the fresh join it is.
  hardDisconnect(namespace) {
    const socket = this.getSocket(namespace);
    if (!socket) return null;
    if (socket.connected) socket.disconnect();
    this.connectedNamespaces.delete(namespace);
    this.connectionCount = Math.max(0, this.connectionCount - 1);
    return socket;
  }

  getSocket(namespace) {
    switch (namespace) {
      case "live-review":
        return liveReviewSocket;
      case "annual-okr-live-review":
        return annualOkrLiveReviewSocket;
      case "quarterly-okr-live-review":
        return quarterlyOkrLiveReviewSocket;
      case "new-chat":
        return coachChatSocket;
      default:
        return null;
    }
  }

  // Only disconnect all when leaving Projects section entirely
  disconnectAll() {
    if (liveReviewSocket.connected) liveReviewSocket.disconnect();
    this.connectedNamespaces.clear();
    this.connectionCount = 0;
  }

  // Update auth headers for all sockets
  updateAuth() {
    const authToken = resolveSocketAuthToken();
    [liveReviewSocket, coachChatSocket].forEach((socket) => {
      socket.io.opts.extraHeaders = {
        authorization: authToken,
      };
    });
  }
}

// Export singleton instance
export const unifiedProjectSocketManager = new UnifiedProjectSocketManager();
